import React, { useEffect, useState } from 'react';

import { navigate, RouteComponentProps } from '@reach/router';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';

import { IAuthActions, IResetPassword, IResetPasswordForm } from '../../../store/auth/actions';
import { INotificationsActions } from '../../../store/notification/actions';

import Button from '../../../components/Button';
import { FormConsumer, FormProvider, IFormAttributes } from '../../../components/Forms';
import Input from '../../../components/Input';
import { redWaglLogo } from '../../../assets/images/';
import useLocation from '../../../contrib/Hooks/useLocation.hook';
import { strongPassWordErrorMessage } from '../../../contrib/validations';
import { externalRoutes } from '../../../routes/Utils/Routes/external.routes';

interface IProps extends RouteComponentProps {
  actions: {
    auth: IAuthActions;
    notifications: INotificationsActions;
  };
  uid: string;
  token: string;
}

const ResetPassword = (props: IProps) => {
  const {
    actions: {
      auth: { resetPassword },
      notifications: { push: pushNotifications }
    },
    uid,
    token
  } = props;

  const [invalidPassword] = useState(false);
  const [invalidPasswordMessage] = useState('');

  const [queryStringEmail, setQueryStringEmail] = useState<string>();

  const {
    location: { search }
  } = useLocation();

  useEffect(() => {
    const newQueryStringEmail = new URLSearchParams(search).get('email');

    if (newQueryStringEmail) {
      setQueryStringEmail(newQueryStringEmail.replace(' ', '+'));
    }
  }, [search]);

  const onSubmit = async (data: IResetPasswordForm): Promise<void> => {
    delete data.acceptedToS;

    const requestData: IResetPassword = {
      ...data,
      token,
      uid
    };

    const response = await resetPassword(requestData);

    if (response.errors && response.errors.resetPassword) {
      pushNotifications({
        status: 'unread',
        text: response.errors.resetPassword.msg || 'There was an error',
        type: 'error'
      });
    } else {
      pushNotifications({
        status: 'unread',
        text: 'Password reset was successful',
        type: 'success'
      });

      if (queryStringEmail) {
        navigate(`/login?email=${queryStringEmail}`);
      } else {
        navigate('/login');
      }
    }
  };

  return (
    <FormProvider
      defaultValues={{
        acceptedToS: false,
        new_password1: '',
        new_password2: ''
      }}
      onSubmit={onSubmit}
      validations={{
        acceptedToS: queryStringEmail ? 'isTrue' : 'isBoolean',
        new_password1: ['required', 'strongPassword'],
        new_password2: (values: IResetPasswordForm) => values.new_password1 === values.new_password2
      }}
    >
      <FormConsumer>
        {({ formActions: { setValue, toggleValue }, errors }: IFormAttributes) => {
          return (
            <div className="login form wrapper">
              <div>
                <div className="logo-wrapper">
                  <img src={redWaglLogo} className="logo" alt="logo" />
                </div>

                <h1>{queryStringEmail ? `YOU'RE ALMOST DONE` : 'ENTER NEW PASSWORD'}</h1>

                {queryStringEmail ? <h3>Create a password to access your account </h3> : <></>}

                <Input
                  label="PASSWORD"
                  id="new_password1"
                  type="password"
                  onChange={setValue('new_password1')}
                  hasError={errors.new_password1 || invalidPassword}
                  errorMessage={
                    invalidPassword ? invalidPasswordMessage : strongPassWordErrorMessage
                  }
                  fullWidth={true}
                />

                <Input
                  label="CONFIRM NEW PASSWORD"
                  id="new_password2"
                  type="password"
                  onChange={setValue('new_password2')}
                  hasError={errors.new_password2}
                  errorMessage="Passwords must match"
                  fullWidth={true}
                />
                {queryStringEmail ? (
                  <FormControlLabel
                    style={{ marginTop: '20px' }}
                    classes={{ label: 'tos-checkbox-label-staff override' }}
                    control={
                      <Checkbox
                        checkedIcon={<CheckBoxIcon className="form-icon override" />}
                        color="primary"
                        icon={<CheckBoxOutlineBlankIcon className="form-icon override" />}
                        id="accepted-tos"
                        onChange={toggleValue('acceptedToS')}
                        value="on"
                      />
                    }
                    label={
                      <>
                        BY CLICKING THIS BOX I ACKNOWLEDGE I'VE READ AND I AGREE TO WAGL'S&nbsp;
                        <a href={externalRoutes.termsOfService} target="_blank">
                          TERMS OF SERVICE
                        </a>
                        &nbsp;and&nbsp;
                        <a href={externalRoutes.privacyPolicy} target="_blank">
                          PRIVACY POLICY
                        </a>
                      </>
                    }
                  />
                ) : (
                  <></>
                )}

                <Button
                  id="login"
                  variant="contained"
                  color="primary"
                  disabled={errors.FORM}
                  type="submit"
                  className="button button-shadow"
                >
                  CREATE NEW PASSWORD
                </Button>
              </div>
            </div>
          );
        }}
      </FormConsumer>
    </FormProvider>
  );
};

export { ResetPassword };
